import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button } from "@mui/material";
import { Refresh, WhatsApp } from "@mui/icons-material";
import { VISUALLY_HIDDEN } from "src/_modules/AiSupportChat/constants";
import { NOTICE_KINDS } from "src/_modules/AiSupportChat/core/chatReducer";
import { getAllowedHref } from "src/_modules/AiSupportChat/core/linkPolicy";
import {
  WAIT_KINDS,
  describeWait,
} from "src/_modules/AiSupportChat/core/waitTime";

/** How each notice kind looks (plan §3.7). `tooLong` is the composer's. */
const NOTICES = Object.freeze({
  [NOTICE_KINDS.RATE_LIMITED]: { severity: "warning" },
  [NOTICE_KINDS.UNAVAILABLE]: {
    severity: "warning",
    textKey: "errors.unavailable",
  },
  [NOTICE_KINDS.IN_PROGRESS]: {
    severity: "info",
    textKey: "errors.inProgress",
  },
  [NOTICE_KINDS.NETWORK]: { severity: "error", textKey: "errors.network" },
  [NOTICE_KINDS.GENERIC]: { severity: "error" },
  [NOTICE_KINDS.NOT_FOUND]: { severity: "info", textKey: "errors.notFound" },
});

const secondsUntil = (until) => Math.ceil((until - Date.now()) / 1000);

/**
 * «… حاول مرة أخرى بعد {{seconds}} ثانية.», counting down once a second
 * from `until`; calls `onExpire` at 0 (the notice goes away; the composer
 * re-enables on its own, D68). The ticker stops on unmount, so dismissing
 * the notice clears it. A wait over a minute is a daily limit: «… الوقت
 * المتبقي: ١٢ ساعة تقريبًا.» (minutes / hours, `core/waitTime`), turning
 * into the seconds countdown for its last minute.
 *
 * Screen readers get the sentence once, with the starting number: the
 * visible copy is hidden from them, or the alert would be read every second.
 */
const RateLimitText = ({ until, onExpire }) => {
  const { t, i18n } = useTranslation("aiSupport");
  const [seconds, setSeconds] = useState(() => secondsUntil(until));
  const [initialSeconds] = useState(() => Math.max(1, secondsUntil(until)));
  const onExpireRef = useRef(onExpire);
  useLayoutEffect(() => {
    onExpireRef.current = onExpire;
  });

  // Layout effect: a cooldown that already ended is gone before the paint
  useLayoutEffect(() => {
    let timer;
    const tick = () => {
      const left = until - Date.now();
      const next = Math.ceil(left / 1000);
      setSeconds(next);
      if (next <= 0) {
        onExpireRef.current();
        return;
      }
      // Wake up just after the number changes
      timer = setTimeout(tick, left - (next - 1) * 1000 + 20);
    };
    tick();
    return () => clearTimeout(timer);
  }, [until]);

  const sentence = (left) => {
    const wait = describeWait(left, i18n.language);
    return wait.kind === WAIT_KINDS.LONG
      ? t("errors.rateLimitedDaily", { time: wait.text })
      : t("errors.rateLimited", { seconds: wait.seconds });
  };

  return (
    <>
      <span aria-hidden="true">{sentence(seconds)}</span>
      <Box component="span" sx={VISUALLY_HIDDEN}>
        {sentence(initialSeconds)}
      </Box>
    </>
  );
};

const NoticeAlert = ({
  notice,
  onDismiss,
  onRetry,
  whatsappHref,
  onFocusLost,
}) => {
  const { t } = useTranslation("aiSupport");
  const alertRef = useRef(null);
  const { severity, textKey } = NOTICES[notice.kind];

  // Dismiss / Retry / the countdown remove the alert, maybe with focus on
  // one of its buttons: keep focus in the panel (Esc, D37). Runs while the
  // alert is still in the DOM (layout cleanups precede the removal).
  useLayoutEffect(() => {
    const alert = alertRef.current;
    return () => {
      if (alert?.contains(document.activeElement)) onFocusLost();
    };
  }, [onFocusLost]);

  let text;
  if (notice.kind === NOTICE_KINDS.RATE_LIMITED) {
    text = <RateLimitText until={notice.until} onExpire={onDismiss} />;
  } else if (notice.kind === NOTICE_KINDS.GENERIC) {
    text = notice.message || t("errors.generic");
  } else {
    text = t(textKey);
  }

  let action = null;
  if (notice.kind === NOTICE_KINDS.UNAVAILABLE && whatsappHref) {
    action = (
      <Button
        component="a"
        href={whatsappHref}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        variant="outlined"
        color="success"
        startIcon={<WhatsApp />}
        sx={{ textTransform: "none", bgcolor: "background.paper" }}
      >
        {t("footer.whatsapp")}
      </Button>
    );
  } else if (notice.kind === NOTICE_KINDS.NETWORK && onRetry) {
    action = (
      <Button
        size="small"
        color="inherit"
        variant="outlined"
        startIcon={<Refresh />}
        onClick={onRetry}
        sx={{ textTransform: "none" }}
      >
        {t("message.retry")}
      </Button>
    );
  }

  return (
    <Box sx={{ px: 2, pt: 1.5 }}>
      <Alert
        ref={alertRef}
        severity={severity}
        onClose={onDismiss}
        // Not "Close": the header's Close closes the whole panel
        closeText={t("notice.dismiss")}
        data-ai-notice={notice.kind}
      >
        {text}
        {action && <Box sx={{ mt: 1 }}>{action}</Box>}
      </Alert>
    </Box>
  );
};

/**
 * The chat's dismissible inline notice (plan §3.7), shown above the message
 * list (and, for 855, above home):
 *
 * - `rateLimited` (853): a live countdown (minutes / hours for a daily
 *   limit); it disappears at 0.
 * - `unavailable` (852): a WhatsApp button (the `/eligibility` support URL,
 *   through the link policy). Sending stays blocked until it is dismissed
 *   (`getSendBlocker`); the header dot is grey meanwhile.
 * - `inProgress` (854), `notFound` (855).
 * - `network`: Retry, when `onRetry` is passed (the failed answer's Retry).
 * - `generic`: the server's message, else `errors.generic`.
 *
 * Each notice is an `alert`, announced once when it appears. Keep this
 * mounted: its (focusable, empty) wrapper takes focus when a notice
 * disappears with focus inside it.
 *
 * @param {object} props
 * @param {import("src/_modules/AiSupportChat/core/chatReducer").ChatNotice | null} props.notice
 *   a kind this place shows, or null
 * @param {() => void} props.onDismiss
 * @param {() => void} [props.onRetry] `network` only
 * @param {string} [props.whatsappUrl] `unavailable` only
 */
export default function InlineNotice({
  notice,
  onDismiss,
  onRetry,
  whatsappUrl,
}) {
  const wrapperRef = useRef(null);
  const shown = notice && NOTICES[notice.kind] ? notice : null;
  // Reads the ref when called: a notice mounted together with the wrapper
  // (home appears with the 855 notice) runs its effects before the
  // wrapper's ref is attached
  const focusWrapper = useCallback(() => wrapperRef.current?.focus(), []);

  return (
    <Box ref={wrapperRef} tabIndex={-1} sx={{ flexShrink: 0, outline: "none" }}>
      {shown && (
        <NoticeAlert
          // A new notice of another kind is a new alert (announced again)
          key={shown.kind}
          notice={shown}
          onDismiss={onDismiss}
          onRetry={onRetry}
          whatsappHref={getAllowedHref(whatsappUrl)}
          onFocusLost={focusWrapper}
        />
      )}
    </Box>
  );
}
